import React from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { twMerge } from 'tailwind-merge'
import uniqueId from 'lodash/uniqueId'
import { productUrlBuilder, useConfig, useMultiSite } from 'msp-integrations'
import BaseRichTextComponent from '@cms/RichText/BaseRichTextComponent'
import JsonLd from '@atoms/JsonLd/JsonLd'
import { formatRecipe } from '@atoms/JsonLd/JsonLd.utils'
import Headline from '@atoms/Headline/Headline'
import Icons from '@atoms/Icons/Icons'
import type { RecipeProps, IngredientType, RecipeType } from './Recipe.types'

const Recipe = ({ data, product, img, loading, recipeImg }: RecipeProps) => {
  const { formatMessage } = useIntl()
  const { buildUrl } = useMultiSite()
  const {
    theme: { font }
  } = useConfig()

  const messages = defineMessages({
    prepTime: {
      id: 'msp.recipe.prepTime',
      defaultMessage: 'Preparation time:'
    },
    min: {
      id: 'msp.recipe.min',
      defaultMessage: 'minutes'
    },
    prepQuantity: {
      id: 'msp.recipe.prepQuantity',
      defaultMessage: 'Preparation quantity:'
    },
    ingredients: {
      id: 'msp.recipe.ingredients',
      defaultMessage: 'Ingredients'
    },
    prep: {
      id: 'msp.recipe.prep',
      defaultMessage: 'Preparation'
    },
    extraTip: {
      id: 'msp.recipe.extraTip',
      defaultMessage: 'Extra tip'
    }
  })

  const ingredients: IngredientType[] = []
  const prepSteps = []
  const cookTime = data?.cookTime
    ? data?.cookTime
    : data?.cookMinimumTime + ' - ' + data?.cookMaximumTime

  for (let i = 1; i <= 8; i++) {
    if (data?.[('ingredient_' + i) as keyof RecipeType])
      ingredients.push({
        label: data[('ingredient_' + i) as keyof RecipeType] as string,
        product:
          (data[('ingredient_product_' + i) as keyof RecipeType] as string) ||
          undefined
      })
  }

  for (let i = 1; i <= 10; i++) {
    if (data?.[('recipe_' + i) as keyof RecipeType])
      prepSteps.push({
        step: i < 10 ? '0' + i : i,
        label: data[('recipe_' + i) as keyof RecipeType]
      })
  }

  const IngredientLink = ({ ingredient }: { ingredient: IngredientType }) => {
    const label = (
      <p className={`${font.regular} text-14 py-3`}>{ingredient.label}</p>
    )
    if (ingredient.product)
      return (
        <a
          href={buildUrl(
            productUrlBuilder({
              name: '',
              id: ingredient.product
            })
          )}
          className='hover:underline'
        >
          {label}
        </a>
      )
    else return <>{label}</>
  }

  const richTextData = {
    data: {
      richText: {
        value: data?.additionalText?.value || ''
      },
      hasCustomStyle: true
    },
    className: `text-14 mt-4`
  }
  const ExtraTip = () => (
    <div data-testid={'extra-tip'} className='p-6 bg-lightGray lg:mb-12'>
      <Headline tag='h4' className='text-18'>
        {formatMessage(messages.extraTip)}
      </Headline>
      <BaseRichTextComponent {...richTextData} />
    </div>
  )

  return (
    <div className='lg:mx-12 2xl:mx-24'>
      {data && (
        <JsonLd
          type='Recipe'
          object={formatRecipe({
            ...data,
            image: recipeImg,
            ingredients,
            prepSteps
          })}
        />
      )}
      <Headline tag='h1' className='pt-12 pl-4 text-32 lg:pt-28 2xl:pt-40'>
        {data?.name}
      </Headline>
      <Headline tag='h4' className='pl-4 mt-8 text-18 leading-32'>
        <span className='pr-2'>
          <Icons iconName={'TimerIcon'} size='2xl' />
        </span>
        {`${formatMessage(messages.prepTime)} ${cookTime} ${formatMessage(
          messages.min
        )}`}
      </Headline>
      <Headline tag='h4' className='pl-4 mt-4 text-18 leading-32'>
        <span className='pr-2'>
          <Icons iconName={'UserIcon'} size='2xl' />
        </span>
        {formatMessage(messages.prepQuantity)} {data?.recipeYield}
      </Headline>
      <div className='flex flex-col mt-12 md:flex-row'>
        <div
          className={twMerge(`relative p-4 after:mt-6 after:block 
            after:w-full after:border-b after:border-black after:content-[''] md:pr-20 md:after:absolute
            md:after:right-0 md:after:top-6 md:after:mt-0 md:after:h-5/6 md:after:w-px md:after:bg-black 2xl:pr-32
          `)}
        >
          <Headline tag='h4' className='text-18'>
            {formatMessage(messages.ingredients)}
          </Headline>
          {ingredients.map((ingredient) => (
            <IngredientLink
              key={uniqueId('ingredient-item')}
              ingredient={ingredient}
            ></IngredientLink>
          ))}
        </div>

        <div className='p-4 md:pl-20 2xl:pl-32'>
          <Headline tag='h4' className='text-18'>
            {formatMessage(messages.prep)}
          </Headline>
          {prepSteps.map((s) => (
            <p
              key={`step-${s.step}`}
              className={`${font.regular} text-14 flex flex-row py-3`}
            >
              <span className='pr-4 font-bold'>{s.step}</span>
              <span>{s.label}</span>
            </p>
          ))}
        </div>
      </div>
      {data?.additionalText?.value && <ExtraTip />}
      {product && !loading && (
        <div className='flex flex-col items-start mt-6 mb-12 lg:flex-row'>
          <div className='lg:w-1/2'>
            <img className='pt-12' src={img?.link} alt={img?.alt} />
          </div>
          <div className='lg:w-1/2'>{product}</div>
        </div>
      )}
    </div>
  )
}

export default Recipe
